
import { useAction } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { useCallback, useState } from "react";
import { toConvexId } from "@/types/convex";

type CommandType = 
  | "BlankPush"
  | "ClearPasscode"
  | "ClearRestrictionsPassword"
  | "RestartDevice"
  | "SettingsEnablePersonalHotspot"
  | "SettingsDisablePersonalHotspot"
  | "UpdateInventory";

interface CommandMapping {
  [key: string]: CommandType;
}

const commandMapping: CommandMapping = {
  "Update Inventory": "UpdateInventory",
  "Clear Passcode": "ClearPasscode",
  "Clear Screen Time Passcode": "ClearRestrictionsPassword",
  "Restart Device": "RestartDevice",
  "Send Blank Push": "BlankPush",
  "Enable Hotspot": "SettingsEnablePersonalHotspot",
  "Disable Hotspot": "SettingsDisablePersonalHotspot",
};

export const useDeviceCommands = () => {
  const { toast } = useToast();
  const sendCommand = useAction(api.jamf.commands.send);
  const [isLoading, setIsLoading] = useState(false);

  const executeCommand = useCallback(async (deviceId: string, commandName: string) => {
    const command = commandMapping[commandName];
    
    if (!command) {
      toast({
        title: "Error",
        description: `Unknown command: ${commandName}`,
        variant: "destructive",
      });
      return { success: false, error: "Unknown command" };
    }

    try {
      setIsLoading(true);
      await sendCommand({
        device: toConvexId.device(deviceId),
        command
      });

      toast({
        title: "Success",
        description: `${commandName} command sent successfully`,
      });

      return { success: true };
    } catch (error) {
      console.error(`Error executing ${commandName}:`, error);
      
      toast({
        title: "Error",
        description: `Failed to execute ${commandName}. Please try again.`,
        variant: "destructive",
      });

      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    } finally {
      setIsLoading(false);
    }
  }, [sendCommand, toast]);

  return {
    executeCommand,
    isLoading,
  };
};
